<template>
  <div class="places-page">
    <div class="page-title">
      <h1>
        <MapPinIcon size="0.9x"/>
        Places
      </h1>
    </div>
    <div class="page-content">
      <div class="legend">
        <h4>
          Ancestor birth countries
        </h4>
        <ul class="list-unstyled">
          <li v-for="cc in sortedCountryCountsRef" :key="cc.code">
            <div class="flag fi" :class="'fi-' + cc.code.toLowerCase()"></div>
            <div>{{ cc.text }} <span class="count">({{ cc.count }})</span></div>
          </li>
        </ul>
      </div>
      <div class="earth-area">
        <Earth ref="earthRef" mode="globe"/>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.places-page {
  display: flex;
  flex-direction: column;
  padding-top: 1rem;
}

.page-title {
  display: flex;
  justify-content: center;

  svg {
    margin-top: -6px;
    stroke-width: 0;
    fill: #aaa;

    :deep(circle) {
      stroke: white;
      fill: white;
    }
  }
}

.page-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 992px) {
    min-height: 500px;
  }
}

.legend {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;

  h4 {
    border-bottom: 1px solid #ddd;
    padding-bottom: 0.25rem;
    color: #888;
  }

  ul {
    padding-left: 0.25rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;;

    li {
      padding: 0 0.25rem;
      display: flex;
      justify-content: flex-start;
      gap: 0.5rem;
      font-size: 0.875rem;
      background-color: rgba(255,255,255,0.3);
    }
  }

  .count {
    font-size: 0.75rem;
    color: #888;
  }
}

.earth-area {
  height: 600px;
  aspect-ratio: 1;
}
</style>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useExploreStore } from '../ExploreStore'
import { useViewScopeStore } from '../ViewScopeStore'
import { usePlaceAnalysis } from './PlaceAnalysis'
import { getSortedValueCounts } from '@/util/AnalysisUtil'
import { CountryCodes } from '@/util/GeoUtil'
import { MapPinIcon } from '@zhuowenli/vue-feather-icons'
import { LoadMode } from '@/util/AsyncData'
import Earth from '@/explore/Earth.vue'

interface CountryCount {
  code: string
  text: string
  count: number
}

const sortedCountryCountsRef = ref<CountryCount[]>([])
const earthRef = ref<InstanceType<typeof Earth>>()

const exploreStore = useExploreStore()
const viewScopeStore = useViewScopeStore()
const placeAnalysis = usePlaceAnalysis()

const scopeInfoRef = computed(() => viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data)

watch(scopeInfoRef, () => {
  if (scopeInfoRef.value) {
    const countryCodes = [...placeAnalysis.getScopeCountryCodes(scopeInfoRef.value)]
    sortedCountryCountsRef.value = getSortedValueCounts(countryCodes)
      .map(vc => ({ code: vc.value, text: CountryCodes.toName(vc.value)!, count: vc.count }))
      .sort((a, b) => b.count - a.count || a.text.localeCompare(b.text))
  }
}, { immediate: true })

watch(sortedCountryCountsRef, refreshMarkers)

onMounted(() => {
  // only move to client location if available on page load
  const clientLocation = exploreStore.asyncClientLocation.data
  if (clientLocation) {
    earthRef.value?.addMarker("client", clientLocation)
    earthRef.value?.moveToMarker("client")
  }

  refreshMarkers()
})

function refreshMarkers() {
  earthRef.value?.clearCountryFlags()

  for (const cc of sortedCountryCountsRef.value) {
    earthRef.value?.addCountryFlag(cc.code)
  }
}
</script>
